"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ProductFile {
  name: string;
  url: string;
  type: "pdf" | "design";
}

interface ProductFileDownloadsProps {
  files: ProductFile[];
  productTitle: string;
}

export function ProductFileDownloads({
  files,
  productTitle,
}: ProductFileDownloadsProps) {
  if (!files || files.length === 0) {
    return null;
  }

  const handleDownload = async (file: ProductFile) => {
    try {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(`Error downloading ${file.name}:`, error);
    }
  };

  const handleDownloadAll = async () => {
    for (const file of files) {
      await handleDownload(file);
      // Small delay between downloads to avoid overwhelming the browser
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  // If only one file, show direct download button
  if (files.length === 1) {
    const file = files[0];
    return (
      <Button
        variant='outline'
        size='sm'
        onClick={() => handleDownload(file)}
        title={`Download ${file.type === "pdf" ? "PDF" : "Design"}: ${file.name}`}
      >
        <FileText className='h-4 w-4' />
      </Button>
    );
  }

  // If multiple files, show dropdown menu
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          title={`Download ${files.length} product files for ${productTitle}`}
        >
          <FileText className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        <DropdownMenuItem onClick={handleDownloadAll}>
          <Download className='h-4 w-4 mr-2' />
          Download All ({files.length} files)
        </DropdownMenuItem>
        <div className='border-t my-1' />
        {files.map((file, index) => (
          <DropdownMenuItem
            key={index}
            onClick={() => handleDownload(file)}
            className='flex items-center'
          >
            <FileText className='h-4 w-4 mr-2' />
            <div className='flex flex-col'>
              <span className='text-sm'>{file.name}</span>
              <span className='text-xs text-muted-foreground'>
                {file.type === "pdf" ? "PDF Attachment" : "Design File"}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
